import {
  type OasDocument3XExtended,
  type OasWebPage,
  type OasWebPageApiTable,
  type OasWebPageCustom,
  type OasWebPageGroup,
  type OasWebPageTreeNode,
  type OasWebTabsPage,
  isOasWebMenuNode,
  isOasWebPageApiTable,
  isOasWebPageCustom,
  isOasWebTabsPage,
  safeAbsurd,
} from '@ipfabric/utils';
import { arrayToRecord } from '@ipfabric/utils/fp/arrayToRecord';
import { prop } from '@ipfabric/utils/fp/prop';
import { getPageTreeNodeRequiredPermissions } from 'components/Sidebar/helpers/getPageTreeNodeRequiredPermissions';
import type { MenuTreeNode, UrlListItem } from 'components/Sidebar/menuTree';
import { array } from 'fp-ts';
import { pipe } from 'fp-ts/lib/function';
import { DefaultOasWebPages } from 'utils/oas/constant';
import { getXTableOas } from 'utils/oas/getXTableOas';

/**
 * Create menu structure from OAS definition
 *
 * `msg` is used on menu items to make two legacy components work:
 * - "Intent Verifications" Dashboard widget
 *   `app/pages/Dashboard/Overview/AddWidget/TableColorForm/WidgetOptions/TreeView/TreeView.js`
 * - Search in menu `app/pages/Search/findResults.js`
 *
 * While I am still considering whether it is reasonable to keep such structures on menu tree
 * and how to do it better, we want to avoid refactoring these legacy components in scope of new Tables
 * project, so I decided to build the same msg structure from oas as it was previously hardcoded on table pages.
 * This may be reconsidered in future when search and/or dashboard is refactored to typescript.
 */
export const createMenuFromOas = (
  oas: OasDocument3XExtended | null,
  group: OasWebPageGroup,
): MenuTreeNode[] => {
  if (oas === null) {
    return [];
  }

  const getCustomPageUrlList = (node: OasWebPageCustom): UrlListItem[] => [
    {
      url: node.path,
      msg: { title: node.title },
    },
  ];

  const getTabsPageUrlList = (node: OasWebTabsPage): UrlListItem[] =>
    pipe(
      node.tabs,
      array.chain(getPageTreeNodeUrlList),
      array.prepend<UrlListItem>(node.path),
    );

  const getApiTablePageUrlList = (node: OasWebPageApiTable): UrlListItem[] => {
    const xTable = getXTableOas(oas, node.apiTable);
    return xTable?.webPath
      ? [
          {
            url: xTable.webPath,
            msg: {
              title: xTable.title,
              boxHelpText: xTable.description,
              // columns are used only by menu search (app/pages/Search/findResults.js)
              columns: pipe(
                xTable.columns ?? [],
                arrayToRecord(prop('key'), (xTableColumn) => ({
                  name: xTableColumn.title,
                  help: xTableColumn.description,
                })),
              ),
            },
          },
        ]
      : [];
  };

  const getPageTreeNodeUrlList = (
    node: OasWebTabsPage | OasWebPage,
  ): UrlListItem[] => {
    if (isOasWebPageApiTable(node)) {
      return getApiTablePageUrlList(node);
    }
    if (isOasWebPageCustom(node)) {
      return getCustomPageUrlList(node);
    }
    if (isOasWebTabsPage(node)) {
      return getTabsPageUrlList(node);
    }
    safeAbsurd(node, 'cannot determine menu url list for unknown node');
    return [];
  };

  const createMenuItemFromOasNode = (
    node: OasWebPageTreeNode,
  ): MenuTreeNode => {
    if (isOasWebMenuNode(node)) {
      return {
        label: node.title,
        children: node.submenu.map(createMenuItemFromOasNode),
        'x-required-feature-flag': node['x-required-feature-flag'],
        'x-require-license-feature': node['x-require-license-feature'],
      };
    }
    return {
      apiScopeRequirement: getPageTreeNodeRequiredPermissions(node),
      label: node.title,
      urlList: getPageTreeNodeUrlList(node),
      'x-required-feature-flag': node['x-required-feature-flag'],
      'x-require-license-feature': node['x-require-license-feature'],
    };
  };
  return (oas['x-webPages'] ?? DefaultOasWebPages)[group].map(
    createMenuItemFromOasNode,
  );
};
